/**
 * 手动发送通知表单 Drawer
 * 根据 notification/frontend-spec.md 规范定义
 */

import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Drawer, Form, Select, Input, DatePicker, Button, Space, message } from 'antd';
import dayjs from 'dayjs';
import { sendNotification, pageNotificationTemplates } from '@/services/notification/notificationService';
import type { NotificationTemplateDTO } from '@/types/notification/notificationTemplate';

interface Props {
  visible: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const SendNotificationForm: React.FC<Props> = ({
  visible,
  onClose,
  onSuccess,
}) => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [templates, setTemplates] = useState<NotificationTemplateDTO[]>([]);
  const [selectedTemplate, setSelectedTemplate] = useState<NotificationTemplateDTO | null>(null);

  // 加载模板列表
  useEffect(() => {
    const fetchTemplates = async () => {
      try {
        const response = await pageNotificationTemplates({
          currentPage: 1,
          pageSize: 100,
          isEnabled: true,
        });
        if (response.success && response.data) {
          setTemplates(response.data.tableData);
        }
      } catch (error) {
        console.error('Failed to fetch templates:', error);
      }
    };
    if (visible) {
      fetchTemplates();
    }
  }, [visible]);

  // 模板选择变化
  const handleTemplateChange = (templateCode: string) => {
    const template = templates.find(t => t.templateCode === templateCode);
    setSelectedTemplate(template || null);
  };

  // 提交
  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);

      const response = await sendNotification({
        templateCode: values.templateCode,
        targetType: values.targetType,
        targetId: values.targetId,
        variables: values.variables || {},
        scheduledTime: values.scheduledTime ? values.scheduledTime.valueOf() : undefined,
      });

      if (response.success) {
        message.success(t('notification.record.list.message.sendSuccess'));
        form.resetFields();
        setSelectedTemplate(null);
        onSuccess();
      } else {
        message.error(response.errorMessage || t('common.message.operationFailed'));
      }
    } catch (error) {
      console.error('Failed to send notification:', error);
    } finally {
      setLoading(false);
    }
  };

  // 取消
  const handleCancel = () => {
    form.resetFields();
    setSelectedTemplate(null);
    onClose();
  };

  // 目标类型选项
  const targetTypeOptions = [
    { value: 'USER', label: 'User' },
    { value: 'ORDER', label: 'Order' },
    { value: 'RIDER', label: 'Rider' },
    { value: 'CUSTOMER', label: 'Customer' },
  ];

  return (
    <Drawer
      title={t('notification.record.sendForm.title')}
      placement="right"
      width={600}
      open={visible}
      onClose={handleCancel}
      destroyOnClose
      footer={
        <div style={{ textAlign: 'right' }}>
          <Space>
            <Button onClick={handleCancel}>
              {t('common.cancel')}
            </Button>
            <Button type="primary" loading={loading} onClick={handleSubmit}>
              {t('common.send')}
            </Button>
          </Space>
        </div>
      }
    >
      <Form
        form={form}
        layout="vertical"
        requiredMark="optional"
      >
        <Form.Item
          name="templateCode"
          label={t('notification.record.sendForm.fields.templateCode')}
          rules={[
            { required: true, message: t('notification.record.sendForm.rules.templateCodeRequired') },
          ]}
        >
          <Select
            placeholder={t('notification.record.sendForm.placeholder.templateCode')}
            onChange={handleTemplateChange}
            options={templates.map(t => ({
              value: t.templateCode,
              label: t.templateName,
            }))}
          />
        </Form.Item>

        <Form.Item
          name="targetType"
          label={t('notification.record.sendForm.fields.targetType')}
          rules={[
            { required: true, message: t('notification.record.sendForm.rules.targetTypeRequired') },
          ]}
        >
          <Select
            placeholder={t('notification.record.sendForm.placeholder.targetType')}
            options={targetTypeOptions}
          />
        </Form.Item>

        <Form.Item
          name="targetId"
          label={t('notification.record.sendForm.fields.targetId')}
          rules={[
            { required: true, message: t('notification.record.sendForm.rules.targetIdRequired') },
          ]}
        >
          <Input placeholder={t('notification.record.sendForm.placeholder.targetId')} />
        </Form.Item>

        {selectedTemplate && selectedTemplate.variables && selectedTemplate.variables.length > 0 && (
          <Form.Item
            name="variables"
            label={t('notification.record.sendForm.fields.variables')}
          >
            <div>
              {selectedTemplate.variables.map(variable => (
                <Form.Item
                  key={variable}
                  name={['variables', variable]}
                  label={`${variable}`}
                  style={{ marginBottom: 8 }}
                >
                  <Input placeholder={`Enter ${variable}`} />
                </Form.Item>
              ))}
            </div>
          </Form.Item>
        )}

        <Form.Item
          name="scheduledTime"
          label={t('notification.record.sendForm.fields.scheduledTime')}
        >
          <DatePicker
            showTime
            style={{ width: '100%' }}
            placeholder={t('notification.record.sendForm.placeholder.scheduledTime')}
            disabledDate={(current) => current && current < dayjs().startOf('day')}
          />
        </Form.Item>
      </Form>
    </Drawer>
  );
};

export default SendNotificationForm;
